import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import {
  Grid,
  Typography,
  Paper,
  TextField,
  Box,
  Autocomplete,
  Skeleton,
  FormControl,
  CircularProgress,
  Backdrop,
  Tabs,
  Tab,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import TextInput from "../../Components/InputComponents/TextInput";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";

const styles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    height: "40px",
    maxWidth: `100%`,
    fontFamily: "Geist, sans-serif",
    fontSize: "16px",
    "& fieldset": {
      borderColor: "#C0C0C0",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#C0C0C0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#aac7e7 !important",
      borderWidth: "2px !important",
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#272727",
  },
};

const CreateJob = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    title: "",
    country: "",
    qualification: "",
    profession: "",
    jobID: "",
  };

  const [jobdata, setJobData] = useState({
    JD_Title: "", // req
    JD_Client: null,
    JD_Company: "",
    JD_Locations: null, // req
    JD_Country: null, // req
    JD_SalaryRange: "",
    JD_Currency: null,
    JD_SalaryPay: null,
    JD_MinAge: null,
    JD_MaxAge: null,
    JD_Gender: [],
    JD_Profession: null, // req
    JD_Qualification: [], // req
    JD_Department: "",
    JD_AdditionalReq: [],
    JD_NoofOpening: null,
    JD_OpenDate: null,
    JD_CloseDate: null,
    JD_AdditionalBenefits: "",
    JD_Status: 1, // req
  });

  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  // State for error handling
  const [errors, setErrors] = useState({});

  // State for Suspense handling
  const [loading, setLoading] = useState({
    countries: false, // ok
    currency: false, // ok
    salaryPay: false,
    additionalRequirements: false,
    branches: false, // ok
    qualification: false,
    gender: false, // not ok
    profession: false, // ok
    client: false, // ok
  });

  const [countryList, setCountryList] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);
  const [salaryPayList, setSalaryPayList] = useState([]);
  const [addReqList, setAddReqList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [qualificationList, setQualificationList] = useState([]);
  const [genderList, setGenderList] = useState([]);
  const [professionList, setProfessionList] = useState([]);
  const [clientList, setClientList] = useState([]);

  const filteredOptions = qualificationList.filter(
    (option) => !jobdata?.JD_Qualification.includes(option.QL_Id)
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrors({
      ...errors,
      [name]: "",
    });

    setJobData({
      ...jobdata,
      [name]: value == "" ? null : value,
    });
  };

  // Validate the form fields

  const inputRefs = {
    JD_Title: useRef(),
    JD_Locations: useRef(),
    JD_Country: useRef(),
    JD_Qualification: useRef(),
    JD_Profession: useRef(),
    JD_Client: useRef(),
    JD_OpenDate: useRef(),
    JD_CloseDate: useRef(),
    JD_NoofOpening: useRef(),
  };

  const mandatoryFields = [
    { field: "JD_Title", label: "Job Title", tab: 0 },
    { field: "JD_NoofOpening", label: "Number of Openings", tab: 0 },
    { field: "JD_OpenDate", label: "Open Date", tab: 0 },
    { field: "JD_CloseDate", label: "Close Date", tab: 0 },

    { field: "JD_Profession", label: "Profession", tab: 1 },
    { field: "JD_Country", label: "Country", tab: 1 },
    { field: "JD_Locations", label: "Branch", tab: 1 },
    { field: "JD_Client", label: "Client", tab: 1 },

    { field: "JD_Qualification", label: "Qualification", tab: 3 },
  ];

  useEffect(() => {
    const firstErrorField = mandatoryFields.find(
      (field) => errors[field.field]
    );

    if (firstErrorField && inputRefs[firstErrorField.field]?.current) {
      inputRefs[firstErrorField.field].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      inputRefs[firstErrorField.field].current.focus(); // optional
    }
  }, [errors]);

  const validateForm = () => {
    let formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{20}$/;

    let firstErrorTab = null;

    for (let { field, label, type, tab } of mandatoryFields) {
      const value = jobdata[field];

      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isEmptyValue =
        value === undefined || value === null || value === "";

      if (isEmptyValue || isEmptyArray) {
        formErrors[field] = `${label} is required.`;
        if (firstErrorTab === null) firstErrorTab = tab;
      } else if (type === "email" && !emailRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
        if (firstErrorTab === null) firstErrorTab = tab;
      } else if (jobdata[`JD_NoofOpening`] == 0) {
        formErrors[`JD_NoofOpening`] = `Number of Job Openings cannot be zero`;
      }
    }

    // Special case: open date vs close date
    if (jobdata?.JD_OpenDate && jobdata?.JD_CloseDate) {
      const openDate = new Date(jobdata?.JD_OpenDate);
      const closeDate = new Date(jobdata?.JD_CloseDate);

      if (openDate >= closeDate) {
        formErrors[
          `JD_OpenDate`
        ] = `Open Date must be earlier than Close Date.`;
        formErrors[
          `JD_CloseDate`
        ] = `Open Date must be earlier than Close Date.`;
        if (firstErrorTab === null) firstErrorTab = 0;
      } else if (isNaN(openDate) || isNaN(closeDate)) {
        formErrors[`JD_OpenDate`] = `Date format is Invalid.`;
        formErrors[`JD_CloseDate`] = `Date format is Invalid.`;
        if (firstErrorTab === null) firstErrorTab = 0;
      }
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      if (firstErrorTab !== null) {
        setTabValue(firstErrorTab);
      }

      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });

      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsDisabled(true);
      setLoaderOpen(true);

      axiosAPI
        ?.post(`/Job/add`, jobdata)
        .then((res) => {
          setIsDisabled(false);
          setLoaderOpen(false);

          if (res?.data?.success) {
            successNotify(res?.data?.message);
            setIsDisabled(false);
            setLoaderOpen(false);
            navigate(`/Jobs`, {
              state: { pagination: paginationState, filterData: filterData },
            });
          } else {
            infoNotify(res?.data?.message);
            setIsDisabled(false);
            setLoaderOpen(false);
          }
        })
        .catch((err) => {
          errorNotify("Something went wrong!");
          setIsDisabled(false);
          setLoaderOpen(false);
        });
    }
  };

  const getCountries = async () => {
    try {
      const res = await axiosAPI.get(`/RecruitmentCountry/active/list`);
      setCountryList(res?.data?.data || []);
    } catch (err) {}
  };

  const getCurrency = async () => {
    try {
      setLoading((prev) => ({ ...prev, currency: true }));
      const res = await axiosAPI.get(`/Currency/list`);
      setCurrencyList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, currency: false }));
    }
  };

  const getPayType = async () => {
    try {
      setLoading((prev) => ({ ...prev, salaryPay: true }));
      const res = await axiosAPI.get(`/SalaryPayFrequency/list`);
      setSalaryPayList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, salaryPay: false }));
    }
  };

  const getAdditionalRequirements = async () => {
    try {
      setLoading((prev) => ({ ...prev, additionalRequirements: true }));
      const res = await axiosAPI.get(`/AdditionalRe/active/list`);
      setAddReqList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, additionalRequirements: false }));
    }
  };

  const getBranches = async () => {
    try {
      setLoading((prev) => ({ ...prev, branches: true }));
      const res = await axiosAPI.get(`/Branch/active/list`);
      setBranchList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, branches: false }));
    }
  };

  const getQualification = async () => {
    try {
      setLoading((prev) => ({ ...prev, qualification: true }));
      const res = await axiosAPI.get(`/Qualification/active/list`);
      setQualificationList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, qualification: false }));
    }
  };

  const getGender = async () => {
    try {
      setLoading((prev) => ({ ...prev, gender: true }));
      const res = await axiosAPI.get(`/Gender/list`);
      setGenderList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, gender: false }));
    }
  };

  const getProfession = async () => {
    try {
      setLoading((prev) => ({ ...prev, profession: true }));
      const res = await axiosAPI.get(`/Profession/active/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, profession: false }));
    }
  };

  const getClient = async () => {
    try {
      setLoading((prev) => ({ ...prev, client: true }));
      const res = await axiosAPI.get(`/Client/active/list`);
      setClientList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, client: false }));
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getCountries();
    getCurrency();
    getPayType();
    getAdditionalRequirements();
    getBranches();
    getQualification();
    getGender();
    getProfession();
    getClient();
  }, []);

  const [tabValue, setTabValue] = useState(0);

  const [tabs, setTabs] = useState([
    "Job Details",
    "Department and Profession",
    "Salary",
    "Qualification & Criteria",
  ]);

  const handleSubChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Add Job
      </Typography>

      {/* Tabs ========================================================================================================================================================================== */}
      <Grid container spacing={2} sx={{ maxWidth: "100vw", overflow: `auto` }}>
        <Grid item>
          <Tabs
            value={tabValue}
            onChange={handleSubChange}
            indicatorColor="error"
            textColor="error"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ fontFamily: "Geist, sans-serif", marginTop: "10px" }}
          >
            {tabs.map((label, index) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  fontFamily: "Geist, sans-serif",
                  background: tabValue == index ? "#006CB8" : "#f5f9ffff",
                  border: "1px solid lightgrey",
                  borderBottom: "4px solid #EAB308",
                  color: tabValue == index ? "white" : "black",
                  fontWeight: tabValue == index ? 600 : 500,
                  fontSize: `14px`,
                  letterSpacing: "1px",
                  minWidth: "150px",
                  minHeight: "60px",
                  marginRight: "5px",
                }}
              />
            ))}
          </Tabs>
        </Grid>
      </Grid>

      {/*  Job Details */}
      {tabValue == 0 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/*  Job Title */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Job Title <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="JD_Title"
                placeholder="Enter Job Title"
                name="JD_Title"
                value={jobdata?.JD_Title}
                onChange={handleInputChange}
                error={!!errors?.JD_Title}
                inputRef={inputRefs.JD_Title}
              />
            </Grid>

            {/* Number of Openings */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Number of Openings <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Number of Openings"
                placeholder="Enter the Number of Openings"
                name="JD_NoofOpening"
                value={jobdata?.JD_NoofOpening ? jobdata?.JD_NoofOpening : null}
                onChange={handleInputChange}
                allowOnlyNumbers
                type="text"
                maxLength={3}
                error={!!errors?.JD_NoofOpening}
                inputRef={inputRefs.JD_NoofOpening}
              />
            </Grid>

            {/* Open Date */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Open Date <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Open Date"
                placeholder="Open Date"
                name="JD_OpenDate"
                value={jobdata?.JD_OpenDate}
                onChange={handleInputChange}
                type="date"
                error={!!errors.JD_OpenDate}
                inputRef={inputRefs.JD_OpenDate}
              />
            </Grid>

            {/* Close Date */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Close Date <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Close Date"
                placeholder="Close Date"
                name="JD_CloseDate"
                value={jobdata?.JD_CloseDate}
                onChange={handleInputChange}
                type="date"
                error={!!errors.JD_CloseDate}
                inputRef={inputRefs.JD_CloseDate}
              />
            </Grid>

            {/* Additional Requirements */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Additional Requirements
              </Typography>

              {loading?.additionalRequirements ? (
                <FormControl fullWidth size="small" sx={styles}>
                  <Skeleton
                    variant="rectangular"
                    height={40}
                    width="100%"
                    sx={{ borderRadius: 1 }}
                  />
                </FormControl>
              ) : (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={
                    addReqList?.map((item) => ({
                      value: item?.ARL_Id,
                      label: item?.ARL_Name,
                    })) || []
                  }
                  getOptionLabel={(option) => option.label}
                  value={
                    addReqList
                      ?.filter((item) =>
                        jobdata?.JD_AdditionalReq?.includes(item?.ARL_Id)
                      )
                      .map((item) => ({
                        value: item?.ARL_Id,
                        label: item?.ARL_Name,
                      })) || []
                  }
                  onChange={(event, newValue) => {
                    const selectedValues = Array.isArray(newValue)
                      ? newValue
                      : [];
                    setJobData({
                      ...jobdata,
                      JD_AdditionalReq: selectedValues.map(
                        (option) => option.value
                      ),
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Additional Requirements"
                      size="small"
                      multiline
                      InputProps={{
                        ...params.InputProps,
                        sx: {
                          minHeight: 100,
                          alignItems: "flex-start",
                          paddingTop: 1,
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": {
                      minHeight: 100,
                      alignItems: "flex-start",
                      paddingTop: 1,
                      flexWrap: "wrap",
                    },
                    "& .MuiAutocomplete-input": {
                      marginTop: "8px", // ⬅️ Moves placeholder down
                      color: "grey",
                    },
                    "& .MuiChip-root": {
                      margin: "4px 4px 0 0",
                    },
                  }}
                />
              )}
            </Grid>

            {/* Additional Benefits */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Additional Benefits
              </Typography>
              <TextAreaInput
                placeholder="Additional Benefits"
                name="JD_AdditionalBenefits"
                value={jobdata?.JD_AdditionalBenefits}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Department and Profession */}
      {tabValue == 1 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/* Department */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Department
              </Typography>
              <TextInput
                label="JD_Department"
                placeholder="Enter Department"
                name="JD_Department"
                value={jobdata?.JD_Department}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Profession */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Profession <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={jobdata?.JD_Profession}
                name="JD_Profession"
                onChange={handleInputChange}
                options={professionList?.map((item) => ({
                  value: item?.PI_Id,
                  label: item?.PI_Name,
                }))}
                placeholder="Select Profession"
                error={!!errors.JD_Profession}
                inputRef={inputRefs.JD_Profession}
                loading={loading?.profession}
              />
            </Grid>

            {/* Country */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Country <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={jobdata?.JD_Country}
                name="JD_Country"
                onChange={handleInputChange}
                options={countryList?.map((item) => ({
                  value: item.RC_Id,
                  label: item.RC_Name,
                }))}
                placeholder="Select Country"
                loading={loading?.countries}
                error={!!errors.JD_Country}
                inputRef={inputRefs.JD_Country}
              />
            </Grid>

            {/* Location (Branches) */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Location (Branches) <span style={{ color: "red" }}> * </span>
              </Typography>
              {/* <SelectInput
            value={jobdata?.JD_Locations}
            name="JD_Locations"
            onChange={handleInputChange}
            options={branchList?.map((item) => ({
              value: item?.BL_Id,
              label: item?.BL_Name,
            }))}
            placeholder="Select Branch"
            error={!!errors.JD_Locations}
            inputRef={inputRefs.JD_Locations}
            loading={loading?.branches}
          /> */}

              <TextInput
                label="JD_Locations"
                placeholder="Enter Branch (Location)"
                name="JD_Locations"
                value={jobdata?.JD_Locations}
                onChange={handleInputChange}
                error={!!errors.JD_Locations}
                inputRef={inputRefs.JD_Locations}
              />
            </Grid>

            {/* Client */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Client <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={jobdata?.JD_Client}
                name="JD_Client"
                onChange={handleInputChange}
                options={clientList?.map((item) => ({
                  value: item?.CD_Id,
                  label: item?.CD_Name,
                }))}
                placeholder="Select Client"
                loading={loading?.client}
                error={!!errors.JD_Client}
                inputRef={inputRefs.JD_Client}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Salary Range */}
      {tabValue == 2 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/* Salary Range */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Salary Range
              </Typography>
              <TextInput
                placeholder="Salary Range"
                name={`JD_SalaryRange`}
                value={jobdata?.JD_SalaryRange}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Currency */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Currency
              </Typography>
              <SelectInput
                value={jobdata?.JD_Currency}
                name="JD_Currency"
                onChange={handleInputChange}
                options={currencyList?.map((item) => ({
                  value: item?.CL_Id,
                  label: `${item?.CL_Code} - ${item?.CL_Name}`,
                }))}
                placeholder="Select Currency"
                loading={currencyList?.length == 0 || loading?.currency}
              />
            </Grid>

            {/* Salary Pay Type */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Salary Pay Type
              </Typography>
              <SelectInput
                value={jobdata?.JD_SalaryPay}
                name="JD_SalaryPay"
                onChange={handleInputChange}
                options={salaryPayList?.map((item) => ({
                  value: item?.SPFL_Id,
                  label: item?.SPFL_Name,
                }))}
                placeholder="Select Salary Pay Type"
                loading={salaryPayList?.length == 0 || loading?.salaryPay}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Qualification & Criteria */}
      {tabValue == 3 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/* Qualification */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Qualification <span style={{ color: "red" }}> * </span>
              </Typography>
              {/* <SelectInput
            value={jobdata?.JD_Qualification}
            name="JD_Qualification"
            onChange={handleInputChange}
            options={qualificationList?.map((item) => ({
              value: item?.QL_Id,
              label: item?.QL_Name,
            }))}
            placeholder="Select Qualification"
            error={!!errors.JD_Qualification}
            inputRef={inputRefs.JD_Qualification}
            loading={loading?.qualification}
          /> */}

              <Autocomplete
                multiple
                disableCloseOnSelect
                options={filteredOptions}
                getOptionLabel={(option) => option.QL_Name}
                value={qualificationList.filter((opt) =>
                  jobdata?.JD_Qualification?.includes(opt.QL_Id)
                )}
                onChange={(event, newValue) => {
                  const selectedValues = Array.isArray(newValue)
                    ? newValue
                    : [];
                  setJobData({
                    ...jobdata,
                    JD_Qualification: selectedValues.map(
                      (option) => option.QL_Id
                    ),
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Qualification"
                    size="small"
                    error={!!errors.JD_Qualification}
                  />
                )}
              />
            </Grid>

            {/* Minimum Age */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Minimum Age
              </Typography>
              <TextInput
                label="minAge"
                placeholder="Enter the Minimum Age Limit"
                name="JD_MinAge"
                value={jobdata?.JD_MinAge}
                onChange={handleInputChange}
                allowOnlyNumbers
                maxLength={3}
              />
            </Grid>

            {/*  Maximum Age */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Maximum Age
              </Typography>
              <TextInput
                label="ageLimit"
                placeholder="Enter the Maximum Age Limit"
                name="JD_MaxAge"
                value={jobdata?.JD_MaxAge}
                onChange={handleInputChange}
                allowOnlyNumbers
                maxLength={3}
              />
            </Grid>

            {/* Gender */}
            <Grid
              item
              sx={{ padding: "5px" }}
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={3}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Gender
              </Typography>

              {loading?.gender ? (
                <FormControl fullWidth size="small" sx={styles}>
                  <Skeleton
                    variant="rectangular"
                    height={40}
                    width="100%"
                    sx={{ borderRadius: 1 }}
                  />
                </FormControl>
              ) : (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={
                    genderList?.map((item) => ({
                      value: item?.GL_Id,
                      label: item?.GL_Name,
                    })) || []
                  }
                  getOptionLabel={(option) => option.label}
                  value={
                    genderList
                      ?.filter((item) =>
                        jobdata?.JD_Gender?.includes(item?.GL_Id)
                      )
                      .map((item) => ({
                        value: item?.GL_Id,
                        label: item?.GL_Name,
                      })) || []
                  }
                  onChange={(event, newValue) => {
                    const selectedValues = Array.isArray(newValue)
                      ? newValue
                      : [];
                    setJobData({
                      ...jobdata,
                      JD_Gender: selectedValues.map((option) => option.value),
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Gender"
                      size="small"
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Buttons */}
      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Grid item>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() => {
              navigate(`/Jobs`, {
                state: { pagination: paginationState, filterData: filterData },
              });
            }}
          >
            Cancel
          </CommonButton>
        </Grid>

        {/* Back - Visible if not on first tab */}
        {tabValue > 0 && (
          <Grid item>
            <CommonButton
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => setTabValue((prev) => prev - 1)}
            >
              Back
            </CommonButton>
          </Grid>
        )}

        {/* Next - Visible if not on last tab */}
        {tabValue < tabs?.length - 1 && (
          <Grid item>
            <CommonButton
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setTabValue((prev) => prev + 1)}
            >
              Next
            </CommonButton>
          </Grid>
        )}

        {/* Save - Only visible on last tab */}
        {tabValue === tabs?.length - 1 && (
          <Grid item>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              {isDisabled ? "Saving..." : "Save"}
            </CommonButton>
          </Grid>
        )}
      </Grid>

      {/* Loader Backdrop */}
      <Backdrop
        open={loaderOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default CreateJob;
